import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Menu,
  Checkbox,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Grid,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  HourglassEmpty as PendingIcon,
  Block as BlockedIcon,
  Verified as VerifiedIcon,
  ViewColumn as ViewColumnIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const PendingApprovals = () => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [colMenuAnchorEl, setColMenuAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState({
    user: true,
    companyRole: true,
    contact: true,
    registered: true,
    status: true
  });
  const [selectedTab, setSelectedTab] = useState('pending');
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, unverified: 0 });
  const [groups, setGroups] = useState([]);
  
  // Dialog states
  const [approveDialog, setApproveDialog] = useState({ open: false, user: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, user: null });
  const [selectedGroup, setSelectedGroup] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const visibleColCount =
    (visibleCols.user ? 1 : 0) +
    (visibleCols.companyRole ? 1 : 0) +
    (visibleCols.contact ? 1 : 0) +
    (visibleCols.registered ? 1 : 0) +
    (visibleCols.status ? 1 : 0) +
    1;

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/pending-approvals', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          status: selectedTab === 'all' ? 'all' : selectedTab
        }
      });
      setApprovals(response.data.data || []);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (error) {
      toast.error('Failed to fetch approvals');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, selectedTab]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/approval-stats');
      setStats(response.data.data || {});
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/admin/groups');
      setGroups(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch groups');
    }
  };

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  useEffect(() => {
    fetchStats();
    fetchGroups();
  }, []);

  const handleApprove = async () => {
    if (!approveDialog.user) return;
    
    setActionLoading(true);
    try {
      await api.post(`/admin/approve/${approveDialog.user.id}`, {
        group_id: selectedGroup || undefined
      });
      toast.success(`${approveDialog.user.first_name} ${approveDialog.user.last_name} has been approved!`);
      setApproveDialog({ open: false, user: null });
      setSelectedGroup('');
      fetchApprovals();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.user) return;
    
    setActionLoading(true);
    try {
      await api.post(`/admin/reject/${rejectDialog.user.id}`, {
        reason: rejectReason || undefined
      });
      toast.success(`${rejectDialog.user.first_name} ${rejectDialog.user.last_name} has been rejected`);
      setRejectDialog({ open: false, user: null });
      setRejectReason('');
      fetchApprovals();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject user');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChip = (status, emailVerified) => {
    if (status === 'pending' && !emailVerified) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
          <Chip label="Pending" color="warning" size="small" icon={<PendingIcon />} />
          <Chip label="Email not verified" color="default" size="small" icon={<EmailIcon />} />
        </Box>
      );
    }
    if (!emailVerified && status !== 'approved') {
      return <Chip label="Unverified" color="default" size="small" icon={<EmailIcon />} />;
    }
    switch (status) {
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" icon={<PendingIcon />} />;
      case 'approved':
        return <Chip label="Approved" color="success" size="small" icon={<VerifiedIcon />} />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" icon={<BlockedIcon />} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        User Registration Approvals
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <PendingIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#e65100' }}>
                {stats.pending}
              </Typography>
              <Typography variant="body2" color="text.secondary">Pending</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <VerifiedIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                {stats.approved}
              </Typography>
              <Typography variant="body2" color="text.secondary">Approved</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <BlockedIcon sx={{ fontSize: 40, color: '#f44336', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#c62828' }}>
                {stats.rejected}
              </Typography>
              <Typography variant="body2" color="text.secondary">Rejected</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <EmailIcon sx={{ fontSize: 40, color: '#2196f3', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1565c0' }}>
                {stats.unverified}
              </Typography>
              <Typography variant="body2" color="text.secondary">Unverified</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 2 }}>
        {/* Tabs */}
        <Tabs
          value={selectedTab}
          onChange={(e, v) => { setSelectedTab(v); setPage(0); }}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label={`Pending (${stats.pending})`} value="pending" />
          <Tab label={`Approved (${stats.approved})`} value="approved" />
          <Tab label={`Rejected (${stats.rejected})`} value="rejected" />
          <Tab label="All" value="all" />
        </Tabs>

        {/* Search and Refresh */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by name, email, company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <IconButton onClick={() => { fetchApprovals(); fetchStats(); }} color="primary">
            <RefreshIcon />
          </IconButton>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewColumnIcon fontSize="small" />}
            onClick={(e) => setColMenuAnchorEl(e.currentTarget)}
            type="button"
          >
            Columns
          </Button>
          <Menu
            anchorEl={colMenuAnchorEl}
            open={Boolean(colMenuAnchorEl)}
            onClose={() => setColMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {[
              ['user', 'User'],
              ['companyRole', 'Company / Role'],
              ['contact', 'Contact'],
              ['registered', 'Registered'],
              ['status', 'Status']
            ].map(([key, label]) => (
              <MenuItem
                key={key}
                onClick={() => setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))}
                dense
              >
                <Checkbox size="small" checked={Boolean(visibleCols[key])} />
                <Typography variant="body2">{label}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                {visibleCols.user && <TableCell><strong>User</strong></TableCell>}
                {visibleCols.companyRole && <TableCell><strong>Company / Role</strong></TableCell>}
                {visibleCols.contact && <TableCell><strong>Contact</strong></TableCell>}
                {visibleCols.registered && <TableCell><strong>Registered</strong></TableCell>}
                {visibleCols.status && <TableCell><strong>Status</strong></TableCell>}
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={visibleColCount} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : approvals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColCount} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No registrations found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                approvals.map((user) => (
                  <TableRow key={user.id} hover>
                    {visibleCols.user && <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: '#6366f1' }} />
                        <Box>
                          <Typography variant="subtitle2">
                            {user.first_name} {user.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>}
                    {visibleCols.companyRole && <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {user.company && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <BusinessIcon fontSize="small" sx={{ color: '#666' }} />
                            <Typography variant="body2">{user.company}</Typography>
                          </Box>
                        )}
                        {user.job_title && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <WorkIcon fontSize="small" sx={{ color: '#666' }} />
                            <Typography variant="body2">{user.job_title}</Typography>
                          </Box>
                        )}
                        {user.department && (
                          <Typography variant="caption" color="text.secondary">
                            {user.department}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>}
                    {visibleCols.contact && <TableCell>
                      {user.phone || <Typography variant="caption" color="text.secondary">No phone</Typography>}
                    </TableCell>}
                    {visibleCols.registered && <TableCell>
                      <Typography variant="body2">
                        {format(new Date(user.createdAt), 'MMM dd, yyyy')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(user.createdAt), 'hh:mm a')}
                      </Typography>
                    </TableCell>}
                    {visibleCols.status && <TableCell>
                      {getStatusChip(user.approval_status, user.email_verified)}
                      {user.rejection_reason && (
                        <Tooltip title={user.rejection_reason}>
                          <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5 }}>
                            See reason
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>}
                    <TableCell align="center">
                      {user.approval_status === 'pending' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip
                              title={
                                user.email_verified
                                  ? 'Approve'
                                  : 'Approve now (email not verified yet — approval will allow login)'
                              }
                            >
                              <IconButton
                                color="success"
                                onClick={() => setApproveDialog({ open: true, user })}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                color="error"
                                onClick={() => setRejectDialog({ open: true, user })}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {!user.email_verified && (
                            <Typography variant="caption" color="text.secondary">
                              Email not verified — you can still approve
                            </Typography>
                          )}
                        </Box>
                      )}
                      {user.approval_status !== 'pending' && (
                        <Typography variant="caption" color="text.secondary">
                          {user.approval_status === 'approved' ? 'Active' : 'No action'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Approve Dialog */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#e8f5e9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ApproveIcon color="success" />
            Approve User Registration
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {approveDialog.user && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                You are about to approve <strong>{approveDialog.user.first_name} {approveDialog.user.last_name}</strong> ({approveDialog.user.email})
              </Alert>

              {!approveDialog.user.email_verified && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This user has not verified their email yet. Approving will mark the email as verified and allow them to log in immediately.
                </Alert>
              )}
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Assign to Group (Optional):</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Group</InputLabel>
                <Select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  label="Select Group"
                >
                  <MenuItem value="">
                    <em>No group (assign later)</em>
                  </MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                      {group.description && (
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          - {group.description}
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity="success">
                The user will be notified via email and can immediately login to the system.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setApproveDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <ApproveIcon />}
          >
            Approve User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#ffebee' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RejectIcon color="error" />
            Reject User Registration
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {rejectDialog.user && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are about to reject <strong>{rejectDialog.user.first_name} {rejectDialog.user.last_name}</strong> ({rejectDialog.user.email})
              </Alert>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason for Rejection (Optional)"
                placeholder="Provide a reason that will be included in the rejection email..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Alert severity="error">
                The user will be notified via email and will not be able to login.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <RejectIcon />}
          >
            Reject User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingApprovals;
